import { useEffect, useMemo, useState } from 'react';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    LinearProgress,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { Title, useNotify } from 'react-admin';

import {
    createEmptyStaticPageDraft,
    createStaticPageDraft,
    type StaticPage,
    type StaticPageDraft,
} from '../admin/staticContents';
import {
    createAdminStaticPage,
    deleteAdminStaticPage,
    getAdminStaticPageList,
    updateAdminStaticPage,
} from '../admin/adminStaticPagesApi';
import { BlogContentEditor } from '../blogPages/BlogContentEditor';
import { formatApiDateTime } from '../admin/dateTimeUtils';

function formatDateTime(value: string): string {
    return formatApiDateTime(value);
}

export function StaticContentsPage() {
    const notify = useNotify();
    const [pages, setPages] = useState<StaticPage[]>([]);
    const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
    const [draft, setDraft] = useState<StaticPageDraft>(() => createEmptyStaticPageDraft());
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const selectedPage = useMemo(
        () => pages.find((page) => page.staticPageId === selectedPageId) ?? null,
        [pages, selectedPageId],
    );

    async function loadPages(nextSearch = search, preferredPageId: string | null = selectedPageId) {
        setIsLoading(true);

        try {
            const result = await getAdminStaticPageList({
                page: 1,
                pageSize: 100,
                search: nextSearch,
            });

            setPages(result.items);
            setTotalCount(result.totalCount);

            const nextSelectedPageId =
                preferredPageId && result.items.some((page) => page.staticPageId === preferredPageId)
                    ? preferredPageId
                    : (result.items[0]?.staticPageId ?? null);

            setSelectedPageId(nextSelectedPageId);
            setDraft(
                nextSelectedPageId
                    ? createStaticPageDraft(
                          result.items.find((page) => page.staticPageId === nextSelectedPageId) ?? result.items[0],
                      )
                    : createEmptyStaticPageDraft(),
            );
        } catch (error) {
            setPages([]);
            setTotalCount(0);
            setSelectedPageId(null);
            setDraft(createEmptyStaticPageDraft());

            const message = error instanceof Error ? error.message : 'Unable to load static pages';
            notify(message, { type: 'error' });
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        void loadPages(search);
    }, [search]);

    const isCreating = selectedPage === null;

    const handleDraftChange = (field: keyof StaticPageDraft, value: string) => {
        setDraft((current) => ({
            ...current,
            [field]: value,
        }));
    };

    const handleSelectPage = (page: StaticPage) => {
        setSelectedPageId(page.staticPageId);
        setDraft(createStaticPageDraft(page));
    };

    const handleCreateNew = () => {
        setSelectedPageId(null);
        setDraft(createEmptyStaticPageDraft());
    };

    const handleSave = async () => {
        const title = draft.title.trim();
        const content = draft.content.trim();

        if (!title) {
            notify('Title is required', { type: 'warning' });
            return;
        }

        if (!content) {
            notify('Content is required', { type: 'warning' });
            return;
        }

        setIsSaving(true);

        try {
            const savedPage = selectedPageId
                ? await updateAdminStaticPage(selectedPageId, draft)
                : await createAdminStaticPage(draft);

            notify(selectedPageId ? 'Static page updated' : 'Static page created', { type: 'info' });
            await loadPages(search, savedPage.staticPageId);
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unable to save static page';
            notify(message, { type: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedPage) {
            return;
        }

        if (!window.confirm(`Delete static page "${selectedPage.title}"?`)) {
            return;
        }

        setIsDeleting(true);

        try {
            await deleteAdminStaticPage(selectedPage.staticPageId);
            notify('Static page deleted', { type: 'info' });
            await loadPages(search, null);
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unable to delete static page';
            notify(message, { type: 'error' });
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <Card>
            <Title title="Static Pages" />
            {isLoading || isSaving || isDeleting ? <LinearProgress /> : null}
            <CardContent>
                <Stack spacing={3}>
                    <Stack spacing={1}>
                        <Typography variant="h4">Static Pages</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Manage public static pages through the admin API, including title, slug, and long-form
                            content.
                        </Typography>
                    </Stack>

                    <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2}>
                        <TextField
                            label="Search pages"
                            value={searchInput}
                            onChange={(event) => setSearchInput(event.target.value)}
                            placeholder="Search title, slug, or content"
                            fullWidth
                        />
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <Button variant="outlined" onClick={() => setSearch(searchInput.trim())}>
                                Search
                            </Button>
                            <Button
                                variant="text"
                                onClick={() => {
                                    setSearchInput('');
                                    setSearch('');
                                }}
                            >
                                Clear
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<RefreshRoundedIcon />}
                                onClick={() => void loadPages(search, selectedPageId)}
                            >
                                Refresh
                            </Button>
                            <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={handleCreateNew}>
                                New Page
                            </Button>
                        </Stack>
                    </Stack>

                    <Box
                        sx={{
                            display: 'grid',
                            gap: 3,
                            gridTemplateColumns: {
                                xs: '1fr',
                                lg: '320px minmax(0, 1fr)',
                            },
                        }}
                    >
                        <Paper variant="outlined">
                            <Stack spacing={1} sx={{ p: 2.5 }}>
                                <Typography variant="h6">Pages</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {totalCount} total page{totalCount === 1 ? '' : 's'}
                                    {search ? ` matching "${search}"` : ''}
                                </Typography>
                            </Stack>
                            <Divider />
                            {pages.length === 0 ? (
                                <Box sx={{ p: 2 }}>
                                    <Alert severity="info">
                                        {search
                                            ? 'No static pages match the current search.'
                                            : 'No static pages exist yet. Create the first page to begin.'}
                                    </Alert>
                                </Box>
                            ) : (
                                <List disablePadding sx={{ maxHeight: 560, overflowY: 'auto' }}>
                                    {pages.map((page) => (
                                        <ListItemButton
                                            key={page.staticPageId}
                                            selected={page.staticPageId === selectedPageId}
                                            onClick={() => handleSelectPage(page)}
                                            divider
                                        >
                                            <ListItemText
                                                primary={page.title || 'Untitled static page'}
                                                secondary={`${page.slug || 'No slug'} • Updated ${formatDateTime(page.updatedAt)}`}
                                                primaryTypographyProps={{ fontWeight: 600 }}
                                                secondaryTypographyProps={{ sx: { mt: 0.5 } }}
                                            />
                                        </ListItemButton>
                                    ))}
                                </List>
                            )}
                        </Paper>

                        <Paper variant="outlined">
                            <Stack spacing={2} sx={{ p: 3 }}>
                                <Stack spacing={0.5}>
                                    <Typography variant="h6">
                                        {isCreating ? 'Create Static Page' : 'Edit Static Page'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {isCreating
                                            ? 'Create a new public page. Leave slug empty if the backend should derive it from the title.'
                                            : 'Update the selected static page and save the changes to the backend.'}
                                    </Typography>
                                </Stack>

                                <TextField
                                    label="Title"
                                    value={draft.title}
                                    onChange={(event) => handleDraftChange('title', event.target.value)}
                                    fullWidth
                                />
                                <TextField
                                    label="Slug"
                                    value={draft.slug}
                                    onChange={(event) => handleDraftChange('slug', event.target.value)}
                                    helperText="Used in the public URL. Backend enforces uniqueness."
                                    fullWidth
                                />
                                <BlogContentEditor
                                    label="Content"
                                    value={draft.content}
                                    onChange={(value) => handleDraftChange('content', value)}
                                    required
                                />

                                <Stack spacing={0.5}>
                                    <Typography variant="caption" color="text.secondary">
                                        Created: {selectedPage ? formatDateTime(selectedPage.createdAt) : 'N/A'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Last updated: {selectedPage ? formatDateTime(selectedPage.updatedAt) : 'N/A'}
                                    </Typography>
                                </Stack>

                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                                    <Button variant="contained" onClick={handleSave} disabled={isSaving || isDeleting}>
                                        {isCreating ? 'Create Page' : 'Save Changes'}
                                    </Button>
                                    <Button variant="outlined" onClick={handleCreateNew} disabled={isSaving || isDeleting}>
                                        Reset Draft
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        startIcon={<DeleteOutlineRoundedIcon />}
                                        onClick={handleDelete}
                                        disabled={!selectedPage || isSaving || isDeleting}
                                    >
                                        Delete Page
                                    </Button>
                                </Stack>
                            </Stack>
                        </Paper>
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
}